/**
 * US-094. Reading a failed receive response.
 *
 * The receive endpoint answers a rejection the merchant can act on with
 * `{ message, code, lines }`, where every entry of `lines` names one PO line
 * that could not be pointed at a Shopify inventory item. Before this, the UI
 * showed a fixed "we could not confirm this receipt" string and threw the
 * server's explanation away, so a merchant whose PO was blocked by three
 * deleted variants had no way to learn which lines were at fault — or that
 * relinking them was the fix.
 *
 * The extraction mirrors the CSV import path in PurchaseOrderDetail.vue, which
 * already digs through the several shapes an error reaches us in (axios
 * `response.data`, a fetch-style `body`, a bare `data`, a stringified body).
 */

/** Matches the backend's `error.details.lines` entries. */
export interface IUnresolvableReceiveLine {
  purchaseOrderItemId: number | string;
  sku: string | null;
  productName: string | null;
  /** String, not number: Shopify ids exceed Number.MAX_SAFE_INTEGER. */
  variantId: string | null;
  errorCategory: string;
  customerMessage: string;
}

export interface IReceiveErrorInfo {
  message: string;
  code: string | null;
  lines: IUnresolvableReceiveLine[];
}

/**
 * The code the backend uses when NOTHING in the receive resolved. Only this
 * case gets the per-line treatment: a receive that partially resolves still
 * commits and reports its failures through the existing sync/retry path.
 */
export const NO_RESOLVABLE_INVENTORY_ITEMS = 'RECEIVE_NO_RESOLVABLE_INVENTORY_ITEMS';

/**
 * Only a variant Shopify positively answered "no such thing" for can be fixed
 * by picking a different one. A lookup that failed says nothing about the
 * variant, so relinking there would rewrite a line that was never broken.
 */
export const RELINKABLE_CATEGORY = 'VARIANT_NOT_FOUND';

const asObject = (value: any): any => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

/** The response body, whichever envelope this particular error arrived in. */
const errorBody = (error: any): any => {
  if (!error) return null;
  return asObject(error?.response?.data)
    || asObject(error?.body)
    || asObject(error?.data)
    || null;
};

const isLine = (line: any): boolean =>
  !!line && typeof line === 'object' && line.purchaseOrderItemId != null;

/**
 * @param error - whatever the receive call rejected with
 * @param fallbackMessage - shown when the server sent no message of its own
 */
export function extractReceiveError(error: any, fallbackMessage: string): IReceiveErrorInfo {
  const body = errorBody(error);

  const message = body?.message
    || body?.error
    || error?.message
    || fallbackMessage;

  const lines: IUnresolvableReceiveLine[] = Array.isArray(body?.lines)
    ? body.lines.filter(isLine)
    : [];

  return {
    message,
    code: body?.code || null,
    lines,
  };
}

/**
 * True when the receive was rejected solely because every line points at
 * something Shopify no longer has — the case a relink resolves.
 *
 * Requires the lines as well as the code: without them the UI has nothing to
 * mark, and falling back to the ordinary error toast is more honest than
 * rendering an empty "fix these lines" panel.
 *
 * Requires EVERY line to be relinkable too. The backend sends this same code
 * when the Shopify lookup itself failed — an expired token, a throttle — and
 * those lines get a category a relink cannot fix. Letting them in put the
 * merchant in a dead end: the panel told them to relink the marked lines while
 * the per-line Relink button stayed hidden. Mixed or non-relinkable failures
 * fall through to the ordinary error path instead.
 */
export function isRelinkableReceiveError(info: IReceiveErrorInfo): boolean {
  return info.code === NO_RESOLVABLE_INVENTORY_ITEMS
    && info.lines.length > 0
    && info.lines.every((line) => line.errorCategory === RELINKABLE_CATEGORY);
}
